import { getApiBaseUrl } from '../config/api';
import { ChatRequest, ChatResponse, ChatStreamEvent } from '../types/aiChat';

export interface StreamChatOptions {
  restaurantId: string;
  request: ChatRequest;
  accessToken: string;
  /** Called for each text fragment as the model generates it. */
  onDelta: (text: string) => void;
  /** Called when the assistant starts consulting a data tool. */
  onTool?: (tool: string) => void;
}

/**
 * Consumes POST /ai-chat/ask-stream (Server-Sent Events). RTK Query can't
 * stream, so this is a plain fetch + ReadableStream reader. Resolves with
 * the final ChatResponse from the `done` event — the same DTO /ask returns,
 * so pending-action handling is identical. Throws when streaming is
 * unavailable (caller falls back to the blocking /ask mutation) or when the
 * server emits an `error` event.
 */
export async function streamChat(opts: StreamChatOptions): Promise<ChatResponse> {
  const res = await fetch(
    `${getApiBaseUrl()}/restaurants/${opts.restaurantId}/ai-chat/ask-stream`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${opts.accessToken}`,
      },
      body: JSON.stringify(opts.request),
    }
  );

  const contentType = res.headers.get('content-type') ?? '';
  if (!res.ok || !contentType.includes('text/event-stream') || !res.body) {
    throw new Error(`stream-unavailable:${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let doneResponse: ChatResponse | null = null;

  const handleEventBlock = (block: string) => {
    for (const line of block.split('\n')) {
      if (!line.startsWith('data:')) continue;
      const payload = line.slice(5).trim();
      if (!payload) continue;
      let event: ChatStreamEvent;
      try {
        event = JSON.parse(payload);
      } catch {
        continue; // partial/garbled line
      }
      if (event.type === 'delta' && event.text) {
        opts.onDelta(event.text);
      } else if (event.type === 'tool' && event.tool) {
        opts.onTool?.(event.tool);
      } else if (event.type === 'done' && event.response) {
        doneResponse = event.response;
      } else if (event.type === 'error') {
        throw new Error(event.message || 'Error procesando la consulta.');
      }
    }
  };

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let separatorIndex;
    while ((separatorIndex = buffer.indexOf('\n\n')) >= 0) {
      const block = buffer.slice(0, separatorIndex);
      buffer = buffer.slice(separatorIndex + 2);
      handleEventBlock(block);
    }
  }

  if (!doneResponse) {
    // Connection dropped before the done event — the caller decides whether
    // to keep the partial text; signal the incomplete turn.
    throw new Error('stream-incomplete');
  }
  return doneResponse;
}
